// Patch 008.8 — Deploy-aware footer.
//
// Sticky muted line at the bottom of every page. Renders:
//   <env> · web <frontend-version> · api <worker-version>
// Frontend version is the git short SHA injected at build time by Vite
// (`__BUILD_VERSION__`, see vite.config.ts). Worker version comes from
// /api/health (which reads env.WORKER_VERSION, set at deploy time via
// `--var WORKER_VERSION:<sha>`). If the health fetch fails or the env
// is empty the footer falls back to "unknown" — never blocks render.

import { useEffect, useState } from 'react'
import { api, type HealthResponse } from '../lib/api'

const WEB_VERSION =
  typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev'

export function Footer(): JSX.Element {
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [healthError, setHealthError] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .health()
      .then((h) => {
        if (!cancelled) setHealth(h)
      })
      .catch(() => {
        if (!cancelled) setHealthError(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const env = health?.environment ?? (healthError ? 'unreachable' : '…')
  const apiVersion = health?.version ?? (healthError ? '—' : '…')

  return (
    <footer className="hcc-footer" data-testid="app-footer">
      <span className="hcc-footer__cell" data-testid="footer-env">
        {env}
      </span>
      <span className="hcc-footer__sep">·</span>
      <span className="hcc-footer__cell" data-testid="footer-web-version">
        web <code>{WEB_VERSION}</code>
      </span>
      <span className="hcc-footer__sep">·</span>
      <span className="hcc-footer__cell" data-testid="footer-api-version">
        api <code>{apiVersion}</code>
      </span>
    </footer>
  )
}
